<script>
/**
 * 상품 카테고리 관리 페이지.
 *
 * 기능:
 * - 카테고리 목록 조회 (서버 페이지네이션)
 * - 검색 필터: 키워드(카테고리명) / 노출여부
 * - view_yn 토글: 목록에서 v-switch로 즉시 ON/OFF
 * - 등록 버튼 → CategoryFormDialog (등록 모드) 오픈
 * - 행 클릭 → CategoryFormDialog (수정 모드) 오픈
 * - 수정 모달에서 삭제 가능
 *   - 연결된 유효 상품이 있으면 삭제 불가 (code=6001)
 */
import { getCategoryList, toggleCategoryView, deleteCategory } from '@/api/category.js'
import { getAdminInfo } from '@/utils/token.js'
import CategoryFormDialog from './CategoryFormDialog.vue'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'CategoryListPage',

  components: { CategoryFormDialog, AppPagination },

  data() {
    return {
      /* ── 검색 조건 ── */
      search: {
        keyword: '',
        viewYn: '',
      },

      /** 노출여부 select 옵션 */
      viewYnOptions: [
        { title: '전체', value: '' },
        { title: '노출', value: 'Y' },
        { title: '미노출', value: 'N' },
      ],

      /* ── 목록 데이터 ── */
      items: [],

      /* ── 페이지네이션 ── */
      page: 1,
      size: 10,
      total: 0,

      /* ── UI 상태 ── */
      loading: false,

      /** 등록/수정 다이얼로그 표시 여부 */
      formDialog: false,
      /** 수정 모달에 전달할 카테고리 데이터 (null이면 등록 모드) */
      selectedCategory: null,

      /** 삭제 확인 다이얼로그 */
      deleteDialog: false,
      /** 삭제할 카테고리 (삭제 확인 다이얼로그에서 사용) */
      deleteTarget: null,
      deleting: false,
      deleteErrorMsg: '',

      /** view_yn 토글 중인 카테고리 코드 Set (중복 클릭 방지) */
      togglingSet: new Set(),

      /** 로그인한 관리자 정보 */
      adminInfo: {},
    }
  },

  computed: {
    /** HQ 관리자 여부: 등록/수정/삭제/토글 버튼 노출 제어 */
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    this.fetchList()
  },

  methods: {
    /**
     * 서버에서 카테고리 목록을 조회한다.
     * 검색 조건 + 페이지네이션 파라미터를 전달하고 결과를 items / total에 세팅한다.
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await getCategoryList({
          keyword: this.search.keyword || undefined,
          viewYn:  this.search.viewYn  || undefined,
          page:    this.page,
          size:    this.size,
        })
        if (res.data?.code === 0) {
          const rows = res.data.data?.items || []
          this.items = [...rows].sort((a, b) => {
            const ao = Number.isFinite(Number(a?.orderby)) ? Number(a.orderby) : Number.MAX_SAFE_INTEGER
            const bo = Number.isFinite(Number(b?.orderby)) ? Number(b.orderby) : Number.MAX_SAFE_INTEGER
            if (ao !== bo) return ao - bo
            return String(b?.insertDt || '').localeCompare(String(a?.insertDt || ''))
          })
          this.total = res.data.data?.total || 0
        }
      } catch (e) {
        console.error('[CategoryListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },

    /** 검색 버튼 클릭: 페이지를 1로 리셋 후 조회 */
    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    /** 초기화 버튼 클릭: 검색 조건 + 페이지 초기화 후 재조회 */
    handleReset() {
      this.search = { keyword: '', viewYn: '' }
      this.page = 1
      this.fetchList()
    },

    /** 페이지 변경 (AppPagination emit) */
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    /** 페이지당 항목 수 변경 (AppPagination emit) */
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    /** 등록 버튼 클릭: 등록 모드로 모달 오픈 */
    openCreateDialog() {
      this.selectedCategory = null
      this.formDialog = true
    },

    /**
     * 행 클릭: 수정 모드로 모달 오픈.
     * @param {Object} item - 선택한 카테고리 항목
     */
    openEditDialog(item) {
      this.selectedCategory = { ...item }
      this.formDialog = true
    },

    /** 등록/수정 완료 후 목록 새로고침 */
    onSaved() {
      this.formDialog = false
      this.selectedCategory = null
      this.fetchList()
    },

    /**
     * view_yn 토글 클릭.
     * 중복 클릭을 방지하기 위해 togglingSet으로 처리 중 상태를 관리한다.
     *
     * @param {Object} item - 토글할 카테고리 항목
     * @param {Event} event - 이벤트 버블링 차단용
     */
    async handleToggleView(item) {
      if (!this.isHq) return
      if (this.togglingSet.has(item.categoryCode)) return

      this.togglingSet = new Set([...this.togglingSet, item.categoryCode])
      try {
        const res = await toggleCategoryView(item.categoryCode)
        if (res.data?.code === 0) {
          // splice로 교체하여 Vue 반응성 보장
          const idx = this.items.findIndex(i => i.categoryCode === item.categoryCode)
          if (idx >= 0) {
            const updated = { ...this.items[idx], viewYn: res.data.viewYn }
            this.items.splice(idx, 1, updated)
          }
        }
      } catch (e) {
        console.error('[CategoryListPage] toggleView error:', e)
      } finally {
        const updated = new Set(this.togglingSet)
        updated.delete(item.categoryCode)
        this.togglingSet = updated
      }
    },

    /**
     * 삭제 버튼 클릭: 삭제 확인 다이얼로그 오픈.
     * @param {Object} item - 삭제할 카테고리
     * @param {Event} event - 이벤트 버블링 차단용
     */
    openDeleteDialog(item, event) {
      event.stopPropagation()
      this.deleteTarget = item
      this.deleteErrorMsg = ''
      this.deleteDialog = true
    },

    /** 삭제 확인 다이얼로그에서 "삭제" 클릭 */
    async handleDelete() {
      if (!this.deleteTarget) return
      this.deleting = true
      this.deleteErrorMsg = ''
      try {
        const res = await deleteCategory(this.deleteTarget.categoryCode)
        if (res.data?.code === 0) {
          this.deleteDialog = false
          this.deleteTarget = null
          this.fetchList()
        } else {
          // code=6001: 연결 상품 있음 → 삭제 불가
          this.deleteErrorMsg = res.data?.message || '삭제 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[CategoryListPage] handleDelete error:', e)
        this.deleteErrorMsg = '삭제 중 오류가 발생했습니다.'
      } finally {
        this.deleting = false
      }
    },

    /** 날짜 포맷: YYYY-MM-DD */
    formatDate(dt) {
      return dt ? dt.substring(0, 10) : '-'
    },

    /** 현물구분 코드 → 표시 라벨 */
    spotTypeLabel(code) {
      if (code === 'GOLD') return '금'
      if (code === 'SILVER') return '은'
      return code || '-'
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">상품 카테고리 관리</div>
        <div class="text-body-2 text-grey mt-1">상품 카테고리 조회 및 등록</div>
      </div>
      <!-- 등록 버튼 (HQ 전용) -->
      <v-btn
        v-if="isHq"
        color="primary"
        prepend-icon="mdi-plus"
        @click="openCreateDialog"
      >
        카테고리 등록
      </v-btn>
    </div>

    <!-- 검색 영역 -->
    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row>
          <!-- 키워드 검색 -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search.keyword"
              label="카테고리 검색"
              placeholder="카테고리명"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
          <!-- 노출여부 -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="search.viewYn"
              :items="viewYnOptions"
              item-title="title"
              item-value="value"
              label="노출여부"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>

        <div class="d-flex justify-end ga-2 mt-4 border-t-thin pt-4">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 목록 카드 -->
    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          카테고리 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <!-- 로딩 -->
      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th class="w-1 text-center">번호</th>
            <th>카테고리코드</th>
            <th>카테고리명</th>
            <th class="w-1">현물구분</th>
            <th class="w-1">정렬순서</th>
            <th class="w-1">노출여부</th>
            <th>등록일</th>
            <th v-if="isHq" class="w-1">삭제</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.categoryCode"
            style="cursor: pointer;"
            class="category-row"
            @click="openEditDialog(item)"
          >
            <!-- 번호 -->
            <td class="text-caption text-grey text-center">
              {{ total - (page - 1) * size - index }}
            </td>
            <!-- 카테고리코드 -->
            <td class="text-caption text-grey-darken-1">{{ item.categoryCode }}</td>
            <!-- 카테고리명 -->
            <td class="font-weight-medium">{{ item.categoryName || '-' }}</td>
            <!-- 현물구분 -->
            <td class="text-caption">{{ spotTypeLabel(item.spotType) }}</td>
            <td class="text-caption">{{ item.orderby ?? '-' }}</td>
            <!-- 노출여부 토글 -->
            <td @click.stop>
              <div @click.stop="handleToggleView(item)">
                <v-switch
                  :model-value="item.viewYn === 'Y'"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="!isHq || togglingSet.has(item.categoryCode)"
                  :loading="togglingSet.has(item.categoryCode)"
                />
              </div>
            </td>
            <!-- 등록일 -->
            <td class="text-caption">{{ formatDate(item.insertDt) }}</td>
            <!-- 삭제 버튼 (HQ 전용) -->
            <td v-if="isHq" @click.stop>
              <v-btn
                icon
                size="small"
                variant="text"
                color="error"
                @click="openDeleteDialog(item, $event)"
              >
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </td>
          </tr>

          <!-- 데이터 없음 -->
          <tr v-if="!loading && items.length === 0">
            <td :colspan="isHq ? 8 : 7" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">
                mdi-tag-off-outline
              </v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- 페이지네이션 -->
      <div class="px-4 pb-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>

    <!-- 등록/수정 다이얼로그 -->
    <CategoryFormDialog
      v-model="formDialog"
      :edit-data="selectedCategory"
      :is-hq="isHq"
      @saved="onSaved"
      @deleted="onSaved"
    />

    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3">
          카테고리 삭제
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <!-- 삭제 불가 오류 메시지 -->
          <v-alert
            v-if="deleteErrorMsg"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ deleteErrorMsg }}
          </v-alert>
          <p v-else class="text-body-2">
            <strong>{{ deleteTarget?.categoryName }}</strong> 카테고리를 삭제하시겠습니까?<br>
            <span class="text-grey text-caption">삭제된 카테고리는 복구할 수 없습니다.</span>
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end ga-2">
          <v-btn variant="text" @click="deleteDialog = false; deleteErrorMsg = ''">닫기</v-btn>
          <v-btn
            v-if="!deleteErrorMsg"
            color="error"
            variant="flat"
            :loading="deleting"
            @click="handleDelete"
          >
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.category-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}
</style>
